<template>
  <div class="min-h-screen bg-[#FAFAFA] flex font-sans text-slate-900">
    <div
      v-if="isMobile && menuOpen"
      class="z-50 bg-black/50 fixed inset-0"
    ></div>
    <aside
      class="side-nav py-5 fixed inset-y-0 z-50 bg-white border-r border-gray-200 ease-in-out w-[200px]"
      :class="[!isMobile || menuOpen ? 'left-0' : '-left-200']"
    >
      <div
        class="px-3 w-full flex justify-center gap-2 pt-1 pb-6 bg-white sticky top-0"
      >
        <button
          v-if="isMobile"
          class="text-gray-600 transition-colors p-1.5 cursor-pointer rounded-sm hover:bg-gray-50"
          @click="toggleMenu"
        >
          <MenuIcon class="size-5" />
        </button>
        <RouterLink
          to="/"
          class="inline-flex flex-col items-end justify-center opacity-100 hover:opacity-80 transition-opacity outline-none"
        >
          <img
            src="@/assets/images/logo.svg"
            alt="sortmyprep logo"
            class="w-[111px]"
          />
        </RouterLink>
      </div>
      <div class="flex-1 w-full px-3 space-y-0.5">
        <button
          v-for="nav in navMap"
          :key="nav.label"
          :disabled="nav.isDisabled"
          class="w-full flex items-center justify-start min-h-10 gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 group item"
          :class="[
            route.name?.startsWith(nav.routeName) ||
            route.name === nav.routeName
              ? 'bg-[#E0EDFF] text-[#2563EB] border border-blue/30 shadow-sm font-semibold item--active'
              : nav.routeName === 'sorty-ai'
                ? 'border border-dashed border-blue/50 text-[#1E3456] font-semibold hover:bg-[#F4F7FE]'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 font-medium cursor-pointer',
            nav.isDisabled
              ? '!cursor-not-allowed !text-gray-400 !bg-gray-50/80'
              : '',
          ]"
          @click="handleNavClick(nav)"
        >
          <img
            v-if="nav.routeName === 'sorty-ai'"
            src="@/assets/images/sorty-icon.png"
            alt=""
            class="size-[18px] shrink-0 rounded"
          />
          <component v-else :is="nav.icon" class="size-[18px] item__icon" />
          <span
            class="leading-tight flex-1 min-w-0 text-left"
            :class="
              nav.comingSoon
                ? 'flex flex-col items-start gap-1 text-[13px]'
                : 'text-[14px]'
            "
          >
            <span>{{ nav.label }}</span>
            <ComingSoonBadge v-if="nav.comingSoon" compact />
          </span>
          <LockIcon
            v-if="isNavLocked(nav)"
            class="size-3.5 text-gray-400 shrink-0"
          />
        </button>

        <div class="pt-3 pb-1">
          <p
            class="text-[9px] font-bold text-gray-400 uppercase tracking-[0.12em] px-3 mb-1.5"
          >
            My Tutor
          </p>
          <div class="h-px bg-gray-100 mb-2"></div>
          <button
            v-for="nav in tutorNavMap"
            :key="nav.label"
            class="w-full cursor-pointer flex items-center justify-start min-h-10 gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 group item"
            :class="[
              route.name?.startsWith(nav.routeName) ||
              route.name === nav.routeName
                ? 'bg-[#E0EDFF] text-[#2563EB] border border-blue/30 shadow-sm font-semibold item--active'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 font-medium',
              nav.isDisabled
                ? 'pointer-events-none !text-gray-400 !bg-gray-50 opacity-50'
                : '',
            ]"
            @click="handleNavClick(nav)"
          >
            <component :is="nav.icon" class="size-[18px] item__icon" />
            <span class="text-[14px] leading-tight flex-1 min-w-0 text-left">
              {{ nav.label }}
            </span>
            <LockIcon
              v-if="isNavLocked(nav)"
              class="size-3.5 text-gray-400 shrink-0"
            />
          </button>
        </div>
      </div>
      <!-- Lifecycle banner: trial countdown while trialing, renewal
           countdown when a paid sub ends soon (PIX-465), free-plan CTA once
           nothing is active, nothing otherwise. -->
      <div class="w-full px-3 mt-3">
        <SubscriptionLifecycleBanner variant="sidebar" />
      </div>

      <div class="mt-auto w-full px-3 space-y-1 pt-4 border-t border-gray-100">
        <button
          class="cursor-pointer w-full flex items-center gap-3 py-2.5 px-3 rounded-lg transition-all duration-200 group relative text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-transparent"
          @click="
            logoutUser(() => {
              router.push({ name: 'login' });
            })
          "
        >
          <LogOutIcon class="size-[18px] stroke-[1.5px]" />
          <span class="text-[14px] font-medium leading-tight"> Logout </span>
        </button>
      </div>
    </aside>
    <div
      class="flex-1 flex flex-col min-h-screen transition-all duration-300 ease-in-out lg:ml-[200px]"
    >
      <header
        class="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200/80 h-14 pl-3 pr-6 flex items-center justify-between md:h-16 md:px-8"
      >
        <div class="flex items-center gap-3">
          <template v-if="isMobile">
            <button
              class="text-gray-600 transition-colors p-1.5 cursor-pointer rounded-sm hover:bg-gray-50"
              @click="toggleMenu"
            >
              <MenuIcon class="size-5" />
            </button>
            <RouterLink
              to="/"
              class="inline-flex flex-col items-end justify-center opacity-100 hover:opacity-80 transition-opacity outline-none"
            >
              <img
                src="@/assets/images/logo.svg"
                alt="sortmyprep logo"
                class="w-[111px]"
              />
            </RouterLink>
          </template>
        </div>
        <div class="flex items-center gap-2 md:gap-4">
          <SearchNav :isMobile="isMobile" />

          <NotificationBell mode="student" />

          <div class="relative">
            <a-dropdown
              v-model:open="ddOpen"
              :overlayStyle="{ width: isMobile ? '256px' : '320px' }"
              placement="bottomRight"
              :trigger="['click']"
              :getPopupContainer="(trigger) => trigger.parentNode"
            >
              <div class="my-1">
                <button
                  class="flex items-center gap-2 rounded-lg outline-none ring-offset-2 ring-transparent focus:ring-2 focus:ring-blue-300 transition-all px-2 pr-1 py-1.5 border border-transparent hover:border-gray-200 hover:bg-gray-100/50 md:py-2 md:px-3"
                >
                  <a-avatar
                    :src="user?.avatar || undefined"
                    :size="34"
                    class="border-none! cursor-pointer hover:ring-1 hover:ring-blue-500 bg-blue text-white"
                    >{{ userInitials }}</a-avatar
                  >
                </button>
              </div>
              <template #overlay>
                <a-menu @click="closeDropDown">
                  <div class="px-3 md:px-4 py-3">
                    <div class="flex items-center gap-3">
                      <a-avatar
                        :src="user?.avatar || undefined"
                        :size="48"
                        class="shrink-0 bg-blue text-white"
                        >{{ userInitials }}</a-avatar
                      >
                      <div class="flex-1 min-w-0">
                        <p class="font-bold text-sm text-gray-900 truncate">
                          {{ user?.full_name ?? '' }}
                        </p>
                        <p class="text-xs text-gray-500 font-medium truncate">
                          {{ user?.email ?? '' }}
                        </p>
                      </div>
                    </div>
                    <!-- Stats grid (Questions / Papers / Plan) - coming soon
                    <div class="mt-3 grid grid-cols-3 gap-2">
                      <div class="bg-[#E0EDFF] rounded-lg p-2 text-center border border-blue/20">
                        <p class="text-lg font-bold text-blue">0</p>
                        <p class="text-[9px] font-medium text-gray-600 uppercase tracking-wide mt-0.5">Questions</p>
                      </div>
                      <div class="bg-[#E0EDFF] rounded-lg p-2 text-center border border-blue/20">
                        <p class="text-lg font-bold text-blue">0</p>
                        <p class="text-[9px] font-bold text-gray-600 uppercase tracking-wide mt-0.5">Papers</p>
                      </div>
                      <div class="bg-[#E0EDFF] rounded-lg p-2 text-center border border-blue/20">
                        <p class="text-lg font-bold text-blue">Pro</p>
                        <p class="text-[9px] font-bold text-gray-600 uppercase tracking-wide mt-0.5">Plan</p>
                      </div>
                    </div>
                    -->
                  </div>
                  <!-- Active Curriculum - coming soon
                  <div role="separator" class="bg-[#0000001a] -mx-1 my-1 h-px"></div>
                  <div class="py-1.5 data-[inset]:pl-8 text-xs text-gray-500 uppercase tracking-wider font-bold mt-2 mb-2 px-2">
                    Active Curriculum
                  </div>
                  <div class="px-2 pb-2">
                    <p class="text-xs text-gray-500 mb-2 leading-relaxed">
                      Your content is personalized to this board. Switching changes your entire view.
                    </p>
                  </div>
                  <a-radio-group class="dd-radio w-full" v-model:value="board" @change="closeDropDown">
                    <a-radio :value="'1'">IGCSE</a-radio>
                    <a-radio :value="'2'">Cambridge International AS &amp; A Level</a-radio>
                  </a-radio-group>
                  -->
                  <div
                    role="separator"
                    class="bg-[#0000001a] -mx-1 mb-1 mt-2 h-px"
                  ></div>
                  <a-menu-item
                    key="0"
                    @click="router.push({ name: 'profile' })"
                    class="font-semibold! py-2! hover:bg-[#E0EDFF]!"
                  >
                    Profile &amp; Settings
                  </a-menu-item>
                  <a-menu-item
                    key="2"
                    @click="router.push({ name: 'settings-subscription' })"
                    class="font-semibold! py-2! hover:bg-[#E0EDFF]!"
                  >
                    My Subscription
                  </a-menu-item>
                  <!-- Help & Support - coming soon
                  <a-menu-item key="1" class="font-semibold! py-2! hover:bg-[#E0EDFF]!">
                    Help &amp; Support
                  </a-menu-item>
                  -->
                  <div
                    role="separator"
                    class="bg-[#0000001a] -mx-1 my-1 h-px"
                  ></div>
                  <a-menu-item
                    key="3"
                    class="bg-red! hover:bg-red-100! py-2!"
                    @click="
                      logoutUser(() => {
                        router.push({ name: 'login' });
                      })
                    "
                  >
                    <span class="text-red-600 font-semibold">Log out</span>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
        </div>
      </header>
      <main
        class="flex-1 overflow-x-hidden min-h-[calc(100vh-64px)] text-slate-900 pb-0"
        :class="
          route.name === 'subscription-checkout' ? 'bg-[#F6FAFF]' : 'bg-white'
        "
      >
        <RouterView />
      </main>
      <SortyLiveOverlay
        v-if="isSortyLiveOpen"
        :conversation-uuid="sortyLiveConversationUuid"
        :mode="sortyLiveMode"
        @close="closeSortyLiveSession"
      />
    </div>

    <!-- Hard trial/subscribe gate for pre-launch accounts (PIX-459 + PIX-464
         CR): unclosable, no dismissal state - it clears only when the server
         says the user is no longer trial-eligible (trial started or paid). -->
    <LegacyTrialModal v-if="showLegacyTrialModal" />
  </div>
</template>

<script setup>
import { useWindowSize } from '@vueuse/core';
import { useRoute, useRouter, RouterView } from 'vue-router';
import {
  BookmarkIcon,
  BookOpenIcon,
  LogOutIcon,
  HouseIcon,
  FileTextIcon,
  StarIcon,
  TrophyIcon,
  ChartColumnIcon,
  BellIcon,
  SearchIcon,
  MenuIcon,
  XIcon,
  LockIcon,
  PenToolIcon,
  SparklesIcon,
  Building2Icon,
  ClipboardList,
} from 'lucide-vue-next';
import SearchNav from './search-nav/SearchNav.vue';
import SubscriptionLifecycleBanner from './subscription/SubscriptionLifecycleBanner.vue';
import { useAuthStore } from '@/stores/auth';
import { useActiveBoardStore } from '@/stores/activeBoard';
import { useSortyLiveSession } from '@/composables/useSortyLiveSession';
import { get } from '@/api';
import { PAYWALL_SOURCE } from '@/observability/events';
import {
  trackPaywallExposure,
  trackPaywallIntent,
} from '@/observability/paywall';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

// Legacy trial gate (PIX-464): eligible = never had ANY subscription row
// (server flag). Unclosable and never remembered locally - it re-appears on
// every route until a trial starts or a payment lands. Checkout is exempt so
// "Subscribe now" can actually be completed.
const GATE_EXEMPT_ROUTES = ['subscription-checkout'];
// The PIX-241 "Browse for now" dismissal (per-user localStorage flag) is
// removed for now: the gate shows on every route for trial-eligible users,
// including those who dismissed it before, until a trial starts or a payment
// lands.
const showLegacyTrialModal = computed(
  () =>
    Boolean(authStore.user) &&
    authStore.trialEligible &&
    !GATE_EXEMPT_ROUTES.includes(route.name)
);

const activeBoardStore = useActiveBoardStore();
const {
  sortyLiveMode,
  sortyLiveConversationUuid,
  isSortyLiveOpen,
  closeSortyLiveSession,
} = useSortyLiveSession();
const { logoutUser } = authStore;
const { user, userInitials, isSubscribed } = storeToRefs(authStore);

onMounted(() => {
  authStore.fetchUser();
  activeBoardStore.fetchAndHydrate(get);
  activeBoardStore.fetchBoardCatalog(get);
});
const ddOpen = ref(false);
const menuOpen = ref(false);
const { width } = useWindowSize();

const closeDropDown = () => {
  ddOpen.value = false;
};
const closeMenu = () => {
  menuOpen.value = false;
};
const isMobile = computed(() => {
  return width.value < 1024;
});

const navMap = [
  { label: 'Home', routeName: 'home', icon: HouseIcon },
  { label: 'Topic-wise', routeName: 'topic-wise', icon: BookOpenIcon },
  { label: 'Practice Papers', routeName: 'past-papers', icon: FileTextIcon },
  {
    label: 'Recommended',
    routeName: 'recommended',
    icon: StarIcon,
    requiresSubscription: true,
  },
  {
    label: 'Exam Builder',
    routeName: 'exam-builder',
    icon: PenToolIcon,
    requiresSubscription: true,
  },
  {
    label: 'sorty AI',
    routeName: 'sorty-ai',
    icon: SparklesIcon,
    requiresSubscription: true,
  },
  {
    label: 'Leaderboard',
    routeName: 'leaderboard',
    icon: TrophyIcon,
    requiresSubscription: true,
  },
  {
    label: 'Insights',
    routeName: 'insights',
    icon: ChartColumnIcon,
    requiresSubscription: true,
  },
  {
    label: 'Saved Questions',
    routeName: 'saved-questions',
    icon: BookmarkIcon,
    requiresSubscription: true,
  },
];

// An active subscription (trial included) opens every module; without one
// the Pro modules show a lock and route to checkout.
const isNavLocked = (nav) =>
  !nav.isDisabled && nav.requiresSubscription && !isSubscribed.value;
// PIX-837: tutor-assigned work is never paywalled, so My Tutor stays open
// after a trial or plan lapses.
const tutorNavMap = [
  {
    label: 'My Organisations',
    routeName: 'my-organisation',
    icon: Building2Icon,
  },
  {
    label: 'My Worksheets',
    routeName: 'my-worksheets',
    icon: ClipboardList,
  },
];

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const handleNavClick = (nav) => {
  closeMenu();
  if (nav.isDisabled) return;
  if (isNavLocked(nav)) {
    // The student wanted this module and was sent to checkout instead -
    // the clearest demand signal the paywall produces.
    trackPaywallIntent(nav.routeName, PAYWALL_SOURCE.SIDEBAR);
    router.push({ name: 'subscription-checkout' });
    return;
  }
  router.push({ name: nav.routeName });
};

// Which Pro modules this student is currently being shown as locked. Fires
// once per module per session - the sidebar re-renders on every navigation,
// and counting each one would drown the intent signal in noise.
watch(
  [() => isSubscribed.value, () => route.name],
  () => {
    for (const nav of [...navMap, ...tutorNavMap]) {
      if (isNavLocked(nav)) {
        trackPaywallExposure(nav.routeName, PAYWALL_SOURCE.SIDEBAR);
      }
    }
  },
  { immediate: true }
);

watch(
  () => isMobile.value,
  () => {
    menuOpen.value = false;
  }
);
</script>

<style scoped>
.side-nav {
  @apply overflow-y-auto flex flex-col items-center transition-all duration-200;
  scrollbar-width: none;
}

.item__icon {
  @apply stroke-[1.5px];
}

.item--active :deep(.item__icon) {
  @apply stroke-[2px];
}

.dd-radio > :deep(.ant-radio-wrapper) {
  display: flex;
  margin-inline-end: 0;
  width: 100%;
  padding: 6px 12px;
  border-radius: 4px;
  color: rgba(0, 0, 0, 0.88);
  font-size: 14px;
  font-weight: 600;
}

.dd-radio > :deep(.ant-radio-wrapper:hover) {
  background-color: #e0edff;
}
</style>
